/**
 * L'ONGLET EXÉCUTION D'UN ORDRE — lot 161, 04/11/2026 (eCMR, bêta).
 *
 * Validé dans la démo « Démo eCMR » (vue Exécution, option A, 04/11) et
 * redécoupé le même jour : ce lot ne porte que la carte « Lettres de
 * voiture » — générer, voir, refaire. LOT 162 : la carte « Liens », avec le
 * lien de suivi CLIENT (créer, copier, ouvrir, refaire, couper). Le lien
 * chauffeur et les étapes signées arrivent au lot 163.
 *
 * Bêta EN PARALLÈLE DE GEDMOUV : la lettre Stafa n'a pas encore valeur de
 * document officiel (décision du 04/11).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { EchecApi } from './api.js';
import { genererLesLettres, lireLesLettres, refaireLaLettre } from './apiLettresVoiture.js';
import {
  adresseDeLaMission,
  adresseDuSuivi,
  chauffeursProposes,
  couperLeLien,
  creerLeLienChauffeur,
  creerLeLienClient,
  lireLExecution,
  lireLesLiens,
  etatDeLaLettre,
  executionDeLaLettre,
  toutRecommencer,
  type ChoixChauffeur,
  type EtatDesLiens,
  type ExecutionLue,
  type LienSuivi,
} from './apiLiensSuivi.js';
import { creneau, momentDe } from './suivi.js';
import {
  dateDite,
  ouvrirLePdfDeLettre,
  remplirLettre,
  reserveDite,
  type EtatDesLettres,
  type ExecutionDeLettre,
  type LettreVoiture,
} from './lettreVoiture.js';

/** La lettre ouverte, à l'échelle de la fenêtre. */
function ApercuLettre({
  lettre,
  ex,
  quandFermer,
}: {
  readonly lettre: LettreVoiture;
  readonly ex: ExecutionDeLettre | null;
  readonly quandFermer: () => void;
}) {
  const cadre = useRef<HTMLDivElement>(null);
  const [echelle, setEchelle] = useState(1);
  useEffect(() => {
    const ajuster = () => {
      const largeur = cadre.current?.clientWidth ?? 800;
      // 595 pt = 793 px : la page A4 du modèle.
      setEchelle(Math.min(1.3, largeur / 793));
    };
    ajuster();
    window.addEventListener('resize', ajuster);
    return () => window.removeEventListener('resize', ajuster);
  }, []);
  return (
    <div className="voile-boite" role="presentation" onClick={quandFermer}>
      <div
        className="boite-lettre-voiture"
        role="dialog"
        aria-modal="true"
        aria-label={`Lettre de voiture ${lettre.numero}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="tete-lettre-voiture">
          <strong>Lettre de voiture {lettre.numero}</strong>
          <button className="bouton bouton-discret" type="button" onClick={quandFermer}>
            Fermer
          </button>
        </div>
        <div className="cadre-lettre-voiture" ref={cadre}>
          <div
            className="page-lettre-voiture"
            style={{ transform: `scale(${String(echelle)})`, height: `${String(1123 * echelle)}px` }}
            // Le modèle figé, rempli par `remplirLettre`, qui échappe chaque
            // valeur saisie. Rien d'autre n'entre ici.
            dangerouslySetInnerHTML={{ __html: remplirLettre(lettre, ex) }}
          />
        </div>
      </div>
    </div>
  );
}

/** Une ligne de la carte « Liens » : l'adresse et ses quatre gestes. */
function LigneLien(p: {
  readonly titre: string;
  readonly lien: LienSuivi;
  readonly adresse: string;
  readonly copie: boolean;
  readonly envoi: boolean;
  readonly quandCopier: (adresse: string) => void;
  readonly quandRefaire: (l: LienSuivi) => void;
  readonly quandCouper: (l: LienSuivi) => void;
}) {
  return (
    <div className="ligne-lien">
      <strong>{p.titre}</strong>
      <code className="adresse-lien">{p.adresse}</code>
      {p.copie && <span className="copie-faite">Copié</span>}
      <a className="bouton bouton-discret" href={p.adresse} target="_blank" rel="noreferrer">
        Ouvrir
      </a>
      <button className="bouton" type="button" onClick={() => p.quandCopier(p.adresse)}>
        Copier
      </button>
      <button className="bouton bouton-discret" type="button" disabled={p.envoi} onClick={() => p.quandRefaire(p.lien)}>
        Refaire
      </button>
      <button className="bouton bouton-discret" type="button" disabled={p.envoi} onClick={() => p.quandCouper(p.lien)}>
        Couper
      </button>
    </div>
  );
}

/** Une étape de l'ordre, telle que la fiche la connaît. */
export interface EtapeDeLaFiche {
  readonly id: string;
  readonly type: string;
  readonly site_nom: string;
  readonly site_ville: string | null;
  readonly jour: string;
  readonly heure_debut: string | null;
  readonly heure_fin: string | null;
}

export function VueExecution(p: {
  readonly etat: EtatDesLettres | null;
  readonly refus: string | null;
  readonly envoi: boolean;
  readonly ouverte: LettreVoiture | null;
  readonly quandGenerer: () => void;
  readonly quandRefaire: (l: LettreVoiture) => void;
  readonly quandVoir: (l: LettreVoiture | null) => void;
  /** LOT 162 — la carte « Liens ». `null` : pas encore lus. */
  readonly liens: readonly LienSuivi[] | null;
  readonly origine: string;
  readonly refusLiens: string | null;
  readonly copie: boolean;
  readonly quandCreerLien: () => void;
  readonly quandCopierLien: (adresse: string) => void;
  readonly quandRefaireLien: (l: LienSuivi) => void;
  readonly quandCouperLien: (l: LienSuivi) => void;
  /** LOT 163 — le lien chauffeur et ce que le chauffeur a fait. */
  readonly copieChauffeur: boolean;
  readonly quandCreerLienChauffeur: (chauffeurId: string) => void;
  /** LOT 165 — le chauffeur se choisit dans la liste ; « Tout recommencer ». */
  readonly choix: ChoixChauffeur | null;
  readonly quandRecommencer: () => void;
  readonly quandCopierLienChauffeur: (adresse: string) => void;
  readonly etapes: readonly EtapeDeLaFiche[];
  readonly execution: ExecutionLue | null;
}) {
  const e = p.etat;
  const client = p.liens?.find((l) => l.genre === 'client') ?? null;
  const chauffeur = p.liens?.find((l) => l.genre === 'chauffeur') ?? null;
  const proposes = p.choix === null ? null : chauffeursProposes(p.choix);
  const [choisi, setChoisi] = useState('');
  // Le chauffeur du planning, seul proposé, est déjà choisi.
  const chauffeurId = proposes?.liste.length === 1 ? (proposes.liste[0]?.id ?? '') : choisi;
  const [confirmer, setConfirmer] = useState(false);
  const faites = new Map((p.execution?.etapes ?? []).map((x) => [x.etape_id, x]));
  return (
    <div className="onglet-execution">
      <section className="fn-bloc bloc-lettres-voiture">
        <div className="tete-bloc-lettres">
          <h3 className="titre-bloc-ot">
            Lettres de voiture <span className="pastille-beta">Bêta</span>
          </h3>
          <button
            className="bouton"
            type="button"
            disabled={p.envoi || e === null || e.couples === 0 || e.couples_sans_lettre === 0}
            onClick={p.quandGenerer}
          >
            Générer les lettres de voiture
          </button>
          <button className="bouton bouton-discret" type="button" disabled={p.envoi} onClick={() => setConfirmer(true)}>
            Tout recommencer
          </button>
        </div>
        {confirmer && (
          <div className="bandeau bandeau-confirmation">
            <span>Tout recommencer ? Lettres, plaques, arrivées, départs et signatures seront retirés et archivés.</span>
            <button
              className="bouton bouton-risque"
              type="button"
              disabled={p.envoi}
              onClick={() => {
                setConfirmer(false);
                p.quandRecommencer();
              }}
            >
              Confirmer
            </button>
            <button className="bouton bouton-discret" type="button" onClick={() => setConfirmer(false)}>
              Annuler
            </button>
          </div>
        )}
        {p.refus !== null && <p className="bandeau bandeau-refus">{p.refus}</p>}
        {e === null ? (
          p.refus === null && <p className="attente">Chargement…</p>
        ) : e.lettres.length === 0 ? (
          <p className="rien">{e.couples === 0 ? 'Aucune marchandise dans le Packaging.' : 'Aucune lettre de voiture.'}</p>
        ) : (
          <table className="table-packaging-fn table-lettres">
            <thead>
              <tr>
                <th>N°</th>
                <th>Trajet</th>
                <th>Générée</th>
                <th>État</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {e.lettres.map((l) => {
                const ex = executionDeLaLettre(p.execution, l);
                // LOT 164 : un chargement signé fige la lettre.
                const signee = (ex?.chargement?.signe_le ?? null) !== null;
                return (
                <tr key={l.id}>
                  <td className="numero-lettre">{l.numero}</td>
                  <td>
                    {l.contenu.expediteur.nom ?? '—'} → {l.contenu.destinataire.nom ?? '—'}
                  </td>
                  <td>
                    {dateDite(l.generee_le)} · {l.generee_par}
                  </td>
                  <td>{etatDeLaLettre(l, ex)}</td>
                  <td className="actions-lettre">
                    <button className="bouton bouton-discret" type="button" onClick={() => p.quandVoir(l)}>
                      Voir
                    </button>
                    <button
                      className="bouton bouton-discret"
                      type="button"
                      disabled={p.envoi || signee}
                      title={signee ? 'Déjà signée' : undefined}
                      onClick={() => p.quandRefaire(l)}
                    >
                      Refaire
                    </button>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      <section className="fn-bloc bloc-liens-suivi">
        <div className="tete-bloc-lettres">
          <h3 className="titre-bloc-ot">Liens</h3>
        </div>
        {p.refusLiens !== null && <p className="bandeau bandeau-refus">{p.refusLiens}</p>}
        {p.liens === null ? (
          p.refusLiens === null && <p className="attente">Chargement…</p>
        ) : (
          <>
            {chauffeur === null ? (
              <form
                className="ligne-lien"
                onSubmit={(x) => {
                  x.preventDefault();
                  if (chauffeurId !== '') p.quandCreerLienChauffeur(chauffeurId);
                }}
              >
                <strong>Lien chauffeur</strong>
                {proposes?.bloque != null ? (
                  <span className="rien">{proposes.bloque}</span>
                ) : (
                  <>
                    <select
                      className="champ"
                      aria-label="Chauffeur"
                      value={chauffeurId}
                      disabled={proposes === null || proposes.liste.length === 1}
                      onChange={(x) => setChoisi(x.target.value)}
                    >
                      {proposes !== null && proposes.liste.length !== 1 && <option value="">Choisir le chauffeur</option>}
                      {(proposes?.liste ?? []).map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.nom}
                        </option>
                      ))}
                    </select>
                    <button className="bouton" type="submit" disabled={p.envoi || chauffeurId === ''}>
                      Créer le lien
                    </button>
                  </>
                )}
              </form>
            ) : (
              <LigneLien
                titre={`Lien chauffeur · ${chauffeur.chauffeur_nom ?? ''}`}
                lien={chauffeur}
                adresse={adresseDeLaMission(p.origine, chauffeur.code)}
                copie={p.copieChauffeur}
                envoi={p.envoi}
                quandCopier={p.quandCopierLienChauffeur}
                quandRefaire={p.quandRefaireLien}
                quandCouper={p.quandCouperLien}
              />
            )}
            {client === null ? (
              <div className="ligne-lien">
                <strong>Lien de suivi client</strong>
                <span className="rien">Aucun lien en service.</span>
                <button className="bouton" type="button" disabled={p.envoi} onClick={p.quandCreerLien}>
                  Créer le lien
                </button>
              </div>
            ) : (
              <LigneLien
                titre="Lien de suivi client"
                lien={client}
                adresse={adresseDuSuivi(p.origine, client.code)}
                copie={p.copie}
                envoi={p.envoi}
                quandCopier={p.quandCopierLien}
                quandRefaire={p.quandRefaireLien}
                quandCouper={p.quandCouperLien}
              />
            )}
          </>
        )}
      </section>

      {/* L'EXÉCUTION PAR LE CHAUFFEUR — lot 163 : plaques, arrivées, départs.
          À part du pointage pendant la bêta (décision du 04/11). */}
      <section className="fn-bloc bloc-liens-suivi">
        <div className="tete-bloc-lettres">
          <h3 className="titre-bloc-ot">Étapes</h3>
          {p.execution?.plaques != null && (
            <span className="plaques-execution">
              {p.execution.plaques.tracteur}
              {p.execution.plaques.remorque === null ? ' · sans remorque' : ` · remorque ${p.execution.plaques.remorque}`}
            </span>
          )}
        </div>
        <table className="table-packaging-fn">
          <thead>
            <tr>
              <th>Étape</th>
              <th>Prévu</th>
              <th>Arrivée</th>
              <th>Départ</th>
              <th>Signé par</th>
              <th>Réserve</th>
            </tr>
          </thead>
          <tbody>
            {p.etapes.map((x) => {
              const f = faites.get(x.id);
              return (
                <tr key={x.id}>
                  <td>
                    <strong>{x.type === 'chargement' ? 'Chargement' : 'Livraison'}</strong>
                    <br />
                    {x.site_nom}
                    {x.site_ville !== null && `, ${x.site_ville}`}
                  </td>
                  <td>{creneau({ jour: x.jour, debut: x.heure_debut?.slice(0, 5) ?? null, fin: x.heure_fin?.slice(0, 5) ?? null } as Parameters<typeof creneau>[0])}</td>
                  <td>
                    {f === undefined ? '—' : momentDe(f.arrive_le)}
                    {f !== undefined && !f.arrive_gps && <span className="rien"> · sans position</span>}
                  </td>
                  <td>{f === undefined ? '—' : momentDe(f.parti_le)}</td>
                  <td>{f?.signe_le == null ? '—' : (f.signataire ?? 'Signé')}</td>
                  <td>{f?.signe_le == null ? '—' : reserveDite(f, x.type === 'chargement' ? 'Expéditeur' : 'Destinataire') || '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>
      {p.ouverte !== null && (
        <ApercuLettre
          lettre={p.ouverte}
          ex={executionDeLaLettre(p.execution, p.ouverte)}
          quandFermer={() => p.quandVoir(null)}
        />
      )}
    </div>
  );
}

export function OngletExecution({
  jeton,
  otId,
  etapes,
  quandAccesPerdu,
}: {
  readonly jeton: () => Promise<string | null>;
  readonly otId: string;
  readonly etapes: readonly EtapeDeLaFiche[];
  readonly quandAccesPerdu: () => void;
}) {
  const [etat, setEtat] = useState<EtatDesLettres | null>(null);
  const [refus, setRefus] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const [ouverte, setOuverte] = useState<LettreVoiture | null>(null);
  const [liens, setLiens] = useState<readonly LienSuivi[] | null>(null);
  const [choix, setChoix] = useState<ChoixChauffeur | null>(null);
  const poserLesLiens = (e: EtatDesLiens) => {
    setLiens(e.liens);
    setChoix(e.choix);
  };
  const [refusLiens, setRefusLiens] = useState<string | null>(null);
  const [copie, setCopie] = useState(false);
  const [copieChauffeur, setCopieChauffeur] = useState(false);
  const [execution, setExecution] = useState<ExecutionLue | null>(null);

  const traiter = useCallback(
    (r: { etat: 'ok'; valeur: EtatDesLettres } | { etat: 'echec'; cause: EchecApi; message?: string }, defaut: string) => {
      if (r.etat === 'ok') {
        setEtat(r.valeur);
        setRefus(null);
        return;
      }
      if (r.cause === 'acces') quandAccesPerdu();
      setRefus(r.message ?? defaut);
    },
    [quandAccesPerdu],
  );

  useEffect(() => {
    let vivant = true;
    void (async () => {
      const j = await jeton();
      if (j === null) {
        quandAccesPerdu();
        return;
      }
      const r = await lireLesLettres(j, otId);
      if (vivant) traiter(r, 'Les lettres de voiture n’ont pas pu être lues.');
      const l = await lireLesLiens(j, otId);
      if (!vivant) return;
      if (l.etat === 'ok') poserLesLiens(l.valeur);
      else setRefusLiens(l.message ?? 'Les liens n’ont pas pu être lus.');
      const x = await lireLExecution(j, otId);
      if (vivant && x.etat === 'ok') setExecution(x.valeur);
    })();
    return () => {
      vivant = false;
    };
  }, [jeton, otId, quandAccesPerdu, traiter]);

  const agir = (appel: (j: string) => ReturnType<typeof lireLesLettres>, defaut: string) => {
    setEnvoi(true);
    void (async () => {
      const j = await jeton();
      if (j === null) {
        setEnvoi(false);
        quandAccesPerdu();
        return;
      }
      const r = await appel(j);
      setEnvoi(false);
      traiter(r, defaut);
    })();
  };

  /** Une suite d'appels sur les liens ; le dernier résultat devient la liste. */
  const agirSurLesLiens = (
    appels: readonly ((j: string) => Promise<Awaited<ReturnType<typeof lireLesLiens>>>)[],
    defaut: string,
  ) => {
    setEnvoi(true);
    setCopie(false);
    setCopieChauffeur(false);
    void (async () => {
      const j = await jeton();
      if (j === null) {
        setEnvoi(false);
        quandAccesPerdu();
        return;
      }
      for (const appel of appels) {
        const r = await appel(j);
        if (r.etat !== 'ok') {
          setEnvoi(false);
          if (r.cause === 'acces') quandAccesPerdu();
          setRefusLiens(r.message ?? defaut);
          return;
        }
        poserLesLiens(r.valeur);
      }
      setEnvoi(false);
      setRefusLiens(null);
    })();
  };

  return (
    <VueExecution
      etat={etat}
      refus={refus}
      envoi={envoi}
      ouverte={ouverte}
      quandGenerer={() => agir((j) => genererLesLettres(j, otId), 'Les lettres de voiture n’ont pas pu être générées.')}
      quandRefaire={(l) =>
        agir((j) => refaireLaLettre(j, otId, l.id), 'La lettre de voiture n’a pas pu être refaite.')
      }
      quandVoir={setOuverte}
      liens={liens}
      origine={window.location.origin}
      refusLiens={refusLiens}
      copie={copie}
      quandCreerLien={() => agirSurLesLiens([(j) => creerLeLienClient(j, otId)], 'Le lien n’a pas pu être créé.')}
      quandCopierLien={(adresse) => {
        void navigator.clipboard.writeText(adresse).then(
          () => setCopie(true),
          () => setRefusLiens('La copie a été refusée par le navigateur : sélectionnez l’adresse à la main.'),
        );
      }}
      quandRefaireLien={(l) =>
        agirSurLesLiens(
          [
            (j) => couperLeLien(j, otId, l.id),
            (j) =>
              // Un lien chauffeur d'avant le 05/11 n'a pas d'identifiant : il
              // est seulement coupé, le chauffeur se choisit ensuite.
              l.genre === 'chauffeur'
                ? l.chauffeur_id === null
                  ? lireLesLiens(j, otId)
                  : creerLeLienChauffeur(j, otId, l.chauffeur_id)
                : creerLeLienClient(j, otId),
          ],
          'Le lien n’a pas pu être refait.',
        )
      }
      quandCouperLien={(l) => agirSurLesLiens([(j) => couperLeLien(j, otId, l.id)], 'Le lien n’a pas pu être coupé.')}
      copieChauffeur={copieChauffeur}
      quandCreerLienChauffeur={(n) =>
        agirSurLesLiens([(j) => creerLeLienChauffeur(j, otId, n)], 'Le lien chauffeur n’a pas pu être créé.')
      }
      quandCopierLienChauffeur={(adresse) => {
        void navigator.clipboard.writeText(adresse).then(
          () => setCopieChauffeur(true),
          () => setRefusLiens('La copie a été refusée par le navigateur : sélectionnez l’adresse à la main.'),
        );
      }}
      choix={choix}
      quandRecommencer={() => {
        setEnvoi(true);
        void (async () => {
          const j = await jeton();
          if (j === null) {
            setEnvoi(false);
            quandAccesPerdu();
            return;
          }
          const r = await toutRecommencer(j, otId);
          if (r.etat !== 'ok') {
            setEnvoi(false);
            if (r.cause === 'acces') quandAccesPerdu();
            setRefus(r.message ?? 'Rien n’a été remis à zéro.');
            return;
          }
          // TOUT SE RELIT : lettres, liens, exécution.
          traiter(await lireLesLettres(j, otId), 'Les lettres de voiture n’ont pas pu être lues.');
          const l = await lireLesLiens(j, otId);
          if (l.etat === 'ok') poserLesLiens(l.valeur);
          const x = await lireLExecution(j, otId);
          if (x.etat === 'ok') setExecution(x.valeur);
          setEnvoi(false);
        })();
      }}
      etapes={etapes}
      execution={execution}
    />
  );
}

/**
 * LES LETTRES SIGNÉES DANS L'ONGLET DOCUMENTS — lot 166 (05/11) : elles y
 * apparaissent d'office, dès la signature au chargement, avec « Voir » et
 * « PDF ». AUCUN FICHIER N'EST STOCKÉ : un document de l'onglet est envoyé par
 * le navigateur d'une personne connectée (`apps/api/src/documents.ts`), et le
 * téléphone du chauffeur n'a pas de compte. La lettre est relue de la base,
 * figée à la signature.
 */
export function VueDocumentsECMR(p: {
  readonly lettres: readonly LettreVoiture[];
  readonly execution: ExecutionLue | null;
  readonly ouverte: LettreVoiture | null;
  readonly quandVoir: (l: LettreVoiture | null) => void;
}) {
  const signees = p.lettres.filter((l) => (executionDeLaLettre(p.execution, l)?.chargement?.signe_le ?? null) !== null);
  if (signees.length === 0) return null;
  return (
    <div className="documents-ecmr">
      <table className="table-documents">
        <thead>
          <tr>
            <th>Type</th>
            <th>Lettre de voiture</th>
            <th>État</th>
            <th>Signée le</th>
            <th aria-label="Actions" />
          </tr>
        </thead>
        <tbody>
          {signees.map((l) => {
            const ex = executionDeLaLettre(p.execution, l);
            return (
              <tr key={l.id}>
                <td>
                  <span className="type-document">eCMR</span>
                </td>
                <td>
                  N° {l.numero} · {l.contenu.expediteur.nom ?? '—'} → {l.contenu.destinataire.nom ?? '—'}
                </td>
                <td>{etatDeLaLettre(l, ex)}</td>
                <td className="code">{momentDe(ex?.chargement?.signe_le ?? null)}</td>
                <td className="actions-document">
                  <button className="lien-action" type="button" onClick={() => p.quandVoir(l)}>
                    Voir
                  </button>
                  <button className="lien-action" type="button" onClick={() => ouvrirLePdfDeLettre(l, ex)}>
                    PDF
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {p.ouverte !== null && (
        <ApercuLettre lettre={p.ouverte} ex={executionDeLaLettre(p.execution, p.ouverte)} quandFermer={() => p.quandVoir(null)} />
      )}
    </div>
  );
}

/** Le conteneur : lit les lettres et l'exécution de l'ordre. Silencieux en cas d'échec. */
export function DocumentsECMR({ jeton, otId }: { readonly jeton: () => Promise<string | null>; readonly otId: string }) {
  const [lettres, setLettres] = useState<readonly LettreVoiture[]>([]);
  const [execution, setExecution] = useState<ExecutionLue | null>(null);
  const [ouverte, setOuverte] = useState<LettreVoiture | null>(null);
  useEffect(() => {
    let vivant = true;
    void (async () => {
      const j = await jeton();
      if (j === null) return;
      const [l, x] = await Promise.all([lireLesLettres(j, otId), lireLExecution(j, otId)]);
      if (!vivant) return;
      if (l.etat === 'ok') setLettres(l.valeur.lettres);
      if (x.etat === 'ok') setExecution(x.valeur);
    })();
    return () => {
      vivant = false;
    };
  }, [jeton, otId]);
  return <VueDocumentsECMR lettres={lettres} execution={execution} ouverte={ouverte} quandVoir={setOuverte} />;
}
